import { useState, useEffect, useCallback } from "react";
import { useInView } from "react-intersection-observer";

/**
 * Custom hook for infinite scrolling functionality
 * @param {number} initialItems - Initial number of items to show
 * @param {number} itemsPerLoad - Number of items to load on each scroll
 * @param {Object} inViewOptions - Options for useInView hook
 * @param {string} scrollContainerId - ID of the scrollable container element
 * @returns {Object} - Object containing visibleItems, loadMoreRef, and resetItems function
 */
export const useInfiniteScroll = ({
  scrollContainerId = null,
  initialItems = 8,
  itemsPerLoad = 8,
  inViewOptions = { threshold: 0.1 }
}) => {
  const [visibleItems, setVisibleItems] = useState(initialItems);
  const { ref: loadMoreRef, inView } = useInView(inViewOptions);

  // Reset items when initialItems changes
  useEffect(() => {
    setVisibleItems(initialItems);
  }, [initialItems]);

  const resetItems = useCallback(() => {
    setVisibleItems(initialItems);
  }, [initialItems]);

  useEffect(() => {
    if (inView) setVisibleItems((prev) => prev + itemsPerLoad);
  }, [inView, itemsPerLoad]);

  useEffect(() => {
    if (!scrollContainerId) return;

    const handleScroll = () => {
      const scrollableContent = document.getElementById(scrollContainerId);
      if (!scrollableContent) return;

      const currentScrollTop = scrollableContent.scrollTop;
      if (currentScrollTop === 0) {
        resetItems();
      }
    };

    const scrollableContent = document.getElementById(scrollContainerId);
    if (scrollableContent) {
      scrollableContent.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (scrollableContent) {
        scrollableContent.removeEventListener("scroll", handleScroll);
      }
    };
  }, [scrollContainerId, resetItems]);

  return {
    visibleItems,
    loadMoreRef,
    resetItems,
    setVisibleItems
  };
};
